import { createContext, useContext, useEffect, useState, type DependencyList, type ReactNode } from "react";
import { Activity, Database, LogOut, MapPin, PanelLeftClose, PanelLeftOpen } from "lucide-react";
import { Link, NavLink, Outlet } from "react-router-dom";
import { Button } from "@/components/ui/button";

const navItems = [
  { to: "/cities", label: "Cities", icon: MapPin },
  { to: "/runs", label: "Runs", icon: Activity },
  { to: "/data", label: "Data", icon: Database },
];

type HeaderConfig = {
  title: string;
  breadcrumbs?: Array<{ label: string; to?: string }>;
  actions?: ReactNode;
};

const defaultHeader: HeaderConfig = { title: "citybrews", breadcrumbs: [{ label: "Admin" }] };
const AdminHeaderContext = createContext<(config: HeaderConfig) => void>(() => {});

export function useAdminHeader(config: HeaderConfig, deps: DependencyList = []) {
  const setHeader = useContext(AdminHeaderContext);
  useEffect(() => {
    setHeader(config);
    return () => setHeader(defaultHeader);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}

export function AdminShell() {
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem("citybrews-admin-sidebar") === "collapsed");
  const [header, setHeader] = useState<HeaderConfig>(defaultHeader);
  const sidebarWidth = collapsed ? "w-14" : "w-60";

  function toggleSidebar() {
    setCollapsed((value) => {
      const next = !value;
      localStorage.setItem("citybrews-admin-sidebar", next ? "collapsed" : "expanded");
      return next;
    });
  }

  return (
    <AdminHeaderContext.Provider value={setHeader}>
      <div className="flex h-screen overflow-hidden bg-background text-sm text-foreground">
      <aside className={`flex shrink-0 flex-col border-r bg-sidebar transition-[width] ${sidebarWidth}`}>
        <div className={`flex h-14 items-center border-b px-2 ${collapsed ? "justify-center" : "justify-between"}`}>
          <Link to="/cities" className={`font-semibold tracking-tight ${collapsed ? "sr-only" : ""}`}>citybrews</Link>
          <Button variant="ghost" size="icon-sm" onClick={toggleSidebar} aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}>
            {collapsed ? <PanelLeftOpen /> : <PanelLeftClose />}
          </Button>
        </div>
        <nav className="grid gap-1 p-2">
          {navItems.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  `flex h-8 items-center gap-2 rounded-md px-2 text-sm transition-colors ${collapsed ? "justify-center" : ""} ${
                    isActive ? "bg-sidebar-accent text-sidebar-accent-foreground" : "text-muted-foreground hover:bg-sidebar-accent/60 hover:text-foreground"
                  }`
                }
                title={collapsed ? item.label : undefined}
              >
                <Icon className="size-4" />
                {!collapsed && item.label}
              </NavLink>
            );
          })}
        </nav>
        <div className="mt-auto border-t p-2">
          <Button className={`w-full ${collapsed ? "px-0" : "justify-start"}`} variant="ghost" title={collapsed ? "Sign out" : undefined} onClick={() => fetch("/admin/api/auth/logout", { method: "POST" }).then(() => window.location.reload())}>
            <LogOut />
            {!collapsed && "Sign out"}
          </Button>
        </div>
      </aside>
      <main className="flex min-w-0 flex-1 flex-col">
        <header className="flex h-14 shrink-0 items-center justify-between gap-4 border-b bg-background px-5">
          <div className="min-w-0">
            <div className="flex items-center gap-1 text-xs text-muted-foreground">
              {(header.breadcrumbs ?? []).map((item, index) => (
                <span key={`${item.label}-${index}`} className="inline-flex items-center gap-1">
                  {item.to ? <Link to={item.to} className="hover:text-foreground">{item.label}</Link> : item.label}
                  {index < (header.breadcrumbs?.length ?? 0) - 1 && <span>/</span>}
                </span>
              ))}
            </div>
            <h1 className="truncate text-sm font-semibold tracking-tight">{header.title}</h1>
          </div>
          <div className="flex shrink-0 items-center gap-2">{header.actions}</div>
        </header>
        <div className="min-h-0 flex-1 overflow-auto">
          <Outlet />
        </div>
      </main>
      </div>
    </AdminHeaderContext.Provider>
  );
}
